import type React from "react";
import {
  type RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import _ from "underscore";

import {
  canUserCreateNativeQueries,
  canUserCreateQueries,
  getUserIsAdmin,
} from "metabase/current-user";
import { dayjs } from "metabase/dayjs";
import { useStore } from "metabase/redux";
import type { State } from "metabase/redux/store";
import type {
  MetabotChatContext,
  MetabotSuggestedTransform,
  MetabotTransformInfo,
  TaggedTransform,
} from "metabase-types/api";

export type ChatContextProviderFn = (
  state: State,
  signal?: AbortSignal,
) => Promise<Partial<MetabotChatContext> | void>;

export type DeregisterChatContextProviderFn = () => void;

export type ApplySuggestionPayload = {
  editorTransform: MetabotTransformInfo | undefined;
  suggestedTransform: MetabotSuggestedTransform;
};

export type ApplySuggestionResult =
  | { status: "applied" }
  | { status: "error"; message: string };

export type MetabotSuggestionActions = {
  openTransform: (transform: TaggedTransform) => void;
  applySuggestion: (
    payload: ApplySuggestionPayload,
  ) => Promise<ApplySuggestionResult>;
};

// internal type so we can support tiptap editor and textarea as inputs
export type MetabotPromptInputRef = {
  focus: () => void;
  getValue?: () => string;
  scrollHeight: number;
  scrollTop: number;
};

export type MetabotCtx = {
  prompt: string;
  setPrompt: (prompt: string) => void;
  promptInputRef: RefObject<MetabotPromptInputRef> | undefined;

  getChatContext: (signal?: AbortSignal) => Promise<MetabotChatContext>;
  registerChatContextProvider: (
    fn: ChatContextProviderFn,
  ) => DeregisterChatContextProviderFn;

  // TODO: figure out how to make the enabled types EE only (probably should live in redux imo)
  // suggestionActions: unknown;
  // setSuggestionActions: (actions: unknown) => void;
  suggestionActions: MetabotSuggestionActions | null;
  setSuggestionActions: (actions: MetabotSuggestionActions | null) => void;
};

export const defaultContext: MetabotCtx = {
  prompt: "",
  setPrompt: () => {},
  promptInputRef: undefined,

  getChatContext: () =>
    Promise.resolve({
      user_is_viewing: [],
      current_time_with_timezone: dayjs.tz(dayjs()).format(),
      capabilities: [],
    }),
  registerChatContextProvider: () => () => {},

  suggestionActions: null,
  setSuggestionActions: () => {},
};

export const MetabotContext = createContext<MetabotCtx>(defaultContext);

export const useMetabotContext = () => {
  const context = useContext(MetabotContext);
  if (!context) {
    throw new Error("useMetabotContext must be used within a MetabotProvider");
  }

  return context;
};

export const useRegisterMetabotContextProvider = (
  providerFn: ChatContextProviderFn,
  dependencies: React.DependencyList = [],
) => {
  const { registerChatContextProvider } = useMetabotContext();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cachedProviderFn = useMemo(() => providerFn, dependencies);

  useEffect(() => {
    const deregister = registerChatContextProvider(cachedProviderFn);
    return () => deregister();
  }, [cachedProviderFn, registerChatContextProvider]);
};

const CHAT_CONTEXT_PROVIDER_TIMEOUT_MS = 5000;

const mergeCtx = (
  ctx: MetabotChatContext,
  partialCtx: Partial<MetabotChatContext>,
): MetabotChatContext => {
  return {
    ...ctx,
    ...partialCtx,
    user_is_viewing: partialCtx.user_is_viewing
      ? [...ctx.user_is_viewing, ...partialCtx.user_is_viewing]
      : ctx.user_is_viewing,
  };
};

export const useRegisterMetabotSuggestionActions = (
  actions: MetabotSuggestionActions | null | undefined,
) => {
  const { setSuggestionActions } = useContext(MetabotContext);

  useEffect(() => {
    setSuggestionActions(actions ?? null);
    return () => setSuggestionActions(null);
  }, [actions, setSuggestionActions]);
};

export const MetabotProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  /* Metabot input */
  const [prompt, setPrompt] = useState("");
  const promptInputRef = useRef<MetabotPromptInputRef>(null);
  const [suggestionActions, setSuggestionActions] =
    useState<MetabotSuggestionActions | null>(null);

  /* Metabot context */
  const providerFnsRef = useRef<Set<ChatContextProviderFn>>(new Set());
  const store = useStore();

  const getChatContext = useCallback(
    async (signal?: AbortSignal) => {
      const state = store.getState();
      const providerFns = [...providerFnsRef.current];

      const isAdmin = getUserIsAdmin(state);
      const hasDataAccess = canUserCreateQueries(state);
      const hasNativeWrite = canUserCreateNativeQueries(state);

      let ctx: MetabotChatContext = {
        user_is_viewing: [],
        current_time_with_timezone: dayjs.tz(dayjs()).format(),
        // Unjustified type cast. FIXME
        capabilities: _.compact([
          hasDataAccess && "permission:save_questions",
          hasNativeWrite && "permission:write_sql_queries",
          isAdmin && "permission:write_transforms",
        ]) as string[],
      };

      for (const providerFn of providerFns) {
        if (signal?.aborted) {
          break;
        }

        try {
          let timer: number | undefined;
          let onAbort: (() => void) | undefined;
          try {
            const timeout = new Promise<undefined>((resolve) => {
              timer = window.setTimeout(
                () => resolve(undefined),
                CHAT_CONTEXT_PROVIDER_TIMEOUT_MS,
              );
              onAbort = () => resolve(undefined);
              signal?.addEventListener("abort", onAbort, { once: true });
            });
            const partialCtx = await Promise.race([
              providerFn(state, signal),
              timeout,
            ]);
            if (partialCtx && !signal?.aborted) {
              ctx = mergeCtx(ctx, partialCtx);
            }
          } finally {
            window.clearTimeout(timer);
            if (onAbort) {
              signal?.removeEventListener("abort", onAbort);
            }
          }
        } catch (err) {
          console.error("A metabot chat context provider failed:", err);
        }
      }

      return ctx;
    },
    [store],
  );

  const registerChatContextProvider = useCallback(
    (providerFn: ChatContextProviderFn) => {
      providerFnsRef.current.add(providerFn);
      return () => providerFnsRef.current.delete(providerFn);
    },
    [],
  );

  return (
    <MetabotContext.Provider
      value={{
        prompt,
        setPrompt,
        promptInputRef,
        getChatContext,
        registerChatContextProvider,
        suggestionActions,
        setSuggestionActions,
      }}
    >
      {children}
    </MetabotContext.Provider>
  );
};
